import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Home, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const NotFoundPage = ({ deleted = false }) => {
  const location = useLocation();

  // Set the proper HTTP status hint via document title (for crawlers + analytics)
  useEffect(() => {
    document.title = `Page not found · Zebra One Gallery`;
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_404', { path: location.pathname });
    }
  }, [location.pathname]);

  const eyebrow = deleted ? 'No longer available' : 'Page not found';
  const headline = deleted
    ? 'This page is no longer available.'
    : "We can't find that page.";
  const explanation = deleted
    ? 'The artist or work you were looking for is no longer represented by Zebra One Gallery. Our living collection of contemporary works is just a click away.'
    : "The link you followed may be outdated, or the page has moved as our catalogue evolves. The current collection is just a click away.";

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="not-found-page">
      <Header />

      <section className="flex-1 flex items-center">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-10 py-20 sm:py-32 grid md:grid-cols-12 gap-10 lg:gap-16 items-center w-full">
          <div className="md:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-5"
            >
              {eyebrow}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-serif font-light leading-[1.05] tracking-tight"
              style={{ fontSize: 'clamp(2.4rem, 5.5vw, 4.5rem)' }}
            >
              {headline}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-base text-neutral-600 leading-relaxed mt-7 max-w-xl"
            >
              {explanation}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-9 flex flex-wrap gap-3"
            >
              <Link
                to="/shop"
                className="inline-flex items-center gap-3 px-7 py-3 bg-black text-white text-xs uppercase tracking-[0.25em] hover:bg-neutral-800 transition-colors"
                data-testid="404-shop-cta"
              >
                Browse the collection <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/artists"
                className="inline-flex items-center gap-3 px-7 py-3 border border-black text-xs uppercase tracking-[0.25em] hover:bg-black hover:text-white transition-colors"
                data-testid="404-artists-cta"
              >
                Meet the artists
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-12 pt-8 border-t border-neutral-200 flex flex-wrap gap-x-8 gap-y-3 text-sm"
            >
              <Link to="/" className="inline-flex items-center gap-2 text-neutral-600 hover:text-black">
                <Home className="w-4 h-4" /> Home
              </Link>
              <Link to="/sunday-b-morning" className="text-neutral-600 hover:text-black">Sunday B. Morning</Link>
              <Link to="/news" className="text-neutral-600 hover:text-black">News</Link>
              <Link to="/exhibitions" className="text-neutral-600 hover:text-black">Exhibitions</Link>
              <Link to="/art-consultation" className="text-neutral-600 hover:text-black">Art Consultation</Link>
              <a href="mailto:info@zebraonegallery.com" className="inline-flex items-center gap-2 text-neutral-600 hover:text-black">
                <Mail className="w-4 h-4" /> Get in touch
              </a>
            </motion.div>
          </div>

          {/* Editorial side panel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-5 hidden md:block"
          >
            <div className="aspect-[4/5] relative bg-neutral-900 overflow-hidden">
              <img
                src="/hero-rooms/01_drawing_room.jpg"
                alt="Zebra One Gallery interior"
                className="absolute inset-0 w-full h-full object-cover opacity-90"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-7 text-white">
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-3">
                  Established 1976
                </p>
                <p className="font-serif font-light leading-[1.15] text-2xl">
                  Fifty years championing the art of our time.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NotFoundPage;
